import { Banknote, CreditCard, Plus, QrCode, Trash2, Utensils } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../../../shared/components/Modal';
import { formatBRL } from '../../../shared/lib/format';
import type { FormaPagamento, PagamentoItem } from '../types';

interface Props {
  open: boolean;
  total: number;
  onClose: () => void;
  onConfirm: (pagamentos: PagamentoItem[]) => Promise<void> | void;
  loading?: boolean;
}

const FORMAS: Array<{ forma: FormaPagamento; label: string; icon: React.ElementType }> = [
  { forma: 'dinheiro', label: 'Dinheiro', icon: Banknote },
  { forma: 'pix', label: 'PIX', icon: QrCode },
  { forma: 'cartao_debito', label: 'Debito', icon: CreditCard },
  { forma: 'cartao_credito', label: 'Credito', icon: CreditCard },
  { forma: 'vale_refeicao', label: 'Vale', icon: Utensils },
];

export function PagamentoModal({ open, total, onClose, onConfirm, loading }: Props) {
  const [pagamentos, setPagamentos] = useState<PagamentoItem[]>([]);
  const [formaAtiva, setFormaAtiva] = useState<FormaPagamento>('dinheiro');
  const [valorAtivo, setValorAtivo] = useState('');

  useEffect(() => {
    if (open) {
      setPagamentos([]);
      setFormaAtiva('dinheiro');
      setValorAtivo('');
    }
  }, [open]);

  const pago = useMemo(() => pagamentos.reduce((a, p) => a + p.valor, 0), [pagamentos]);
  const faltante = Math.max(total - pago, 0);
  const troco = Math.max(pago - total, 0);

  const addPagamento = () => {
    const valor = Number(valorAtivo) || faltante;
    if (valor <= 0) return;
    setPagamentos((p) => [...p, { forma: formaAtiva, valor }]);
    setValorAtivo('');
  };

  const removePagamento = (idx: number) => {
    setPagamentos((p) => p.filter((_, i) => i !== idx));
  };

  const pagar = async () => {
    let final = [...pagamentos];
    if (final.length === 0) {
      final = [{ forma: formaAtiva, valor: Number(valorAtivo) || total }];
    }
    await onConfirm(final);
  };

  const pagoFinal = pagamentos.length === 0 ? Number(valorAtivo) || total : pago;
  const podeFinalizar = pagoFinal + 0.01 >= total;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Pagamento"
      size="lg"
      footer={
        <>
          <button className="btn-secondary" onClick={onClose} disabled={loading}>
            Cancelar
          </button>
          <button
            className="btn-primary"
            onClick={pagar}
            disabled={loading || !podeFinalizar}
          >
            {loading ? 'Finalizando…' : `Finalizar ${formatBRL(total)}`}
          </button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="rounded-xl bg-brand-600 p-5 text-white">
          <p className="text-xs uppercase tracking-wide opacity-80">Total a pagar</p>
          <p className="text-3xl font-bold">{formatBRL(total)}</p>
          {pagamentos.length > 0 && (
            <div className="mt-2 flex justify-between border-t border-white/20 pt-2 text-sm">
              <span className="opacity-80">Pago: {formatBRL(pago)}</span>
              {faltante > 0 ? (
                <span className="font-medium">Falta: {formatBRL(faltante)}</span>
              ) : (
                <span className="font-medium">Troco: {formatBRL(troco)}</span>
              )}
            </div>
          )}
        </div>

        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-700">Forma de pagamento</h3>
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
            {FORMAS.map((f) => {
              const Icon = f.icon;
              const active = formaAtiva === f.forma;
              return (
                <button
                  key={f.forma}
                  type="button"
                  onClick={() => setFormaAtiva(f.forma)}
                  className={`flex flex-col items-center gap-1 rounded-lg border p-3 text-xs transition ${
                    active
                      ? 'border-brand-600 bg-brand-50 text-brand-900'
                      : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{f.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">
            Valor {faltante > 0 && <span className="text-xs text-slate-500">(falta {formatBRL(faltante)})</span>}
          </label>
          <div className="flex gap-2">
            <input
              type="number"
              step="0.01"
              min={0}
              className="input text-xl font-semibold"
              value={valorAtivo}
              onChange={(e) => setValorAtivo(e.target.value)}
              placeholder={formatBRL(faltante).replace('R$', '').trim()}
            />
            <button
              type="button"
              className="btn-secondary"
              onClick={addPagamento}
              disabled={faltante <= 0 && pagamentos.length > 0}
            >
              <Plus className="h-4 w-4" />
              Dividir
            </button>
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Deixe em branco para pagar o restante. Use &quot;Dividir&quot; para multiplas formas.
          </p>
        </div>

        {pagamentos.length > 0 && (
          <div>
            <h3 className="mb-2 text-sm font-semibold text-slate-700">Pagamentos adicionados</h3>
            <ul className="space-y-2">
              {pagamentos.map((p, i) => {
                const f = FORMAS.find((x) => x.forma === p.forma);
                const Icon = f?.icon ?? Banknote;
                return (
                  <li
                    key={i}
                    className="flex items-center justify-between rounded-lg border border-slate-200 p-3"
                  >
                    <div className="flex items-center gap-2 text-sm">
                      <Icon className="h-4 w-4 text-slate-500" />
                      <span className="font-medium">{f?.label ?? p.forma}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-semibold">{formatBRL(p.valor)}</span>
                      <button
                        type="button"
                        onClick={() => removePagamento(i)}
                        className="rounded p-1 text-red-500 hover:bg-red-50"
                        aria-label="Remover"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
}
